import { useMemo, useEffect, useCallback } from 'react';
import { db } from '../../../firebase/config';
import { collectionGroup, getDocs, query, where } from 'firebase/firestore';
import { parseCurrency } from '../../../utils/segurosUtils';
import { useReportesMetrics } from '../../../context/ReportesMetricsContext';

// TIEMPO DE VIDA DE LA CACHÉ: 1 HORA (60 minutos * 60 segundos * 1000 ms)
const CACHE_TTL_MS = 1000 * 60 * 60 * 3;

const formatCurrencyARS = (amount) => {
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        maximumFractionDigits: 2
    }).format(amount || 0);
};

const formatCurrencyUSD = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

export const useProduccionMensual = () => {
    const { 
        produccionMensualCache, 
        setProduccionMensualCache, 
        loadingProduccionMensual, 
        setLoadingProduccionMensual 
    } = useReportesMetrics();

    const rangoMesActual = useMemo(() => {
        const hoy = new Date();
        const year = hoy.getFullYear() < 2026 ? 2026 : hoy.getFullYear();
        const month = String(hoy.getMonth() + 1).padStart(2, '0');
        const lastDay = new Date(year, hoy.getMonth() + 1, 0).getDate();
        const nombreMes = hoy.toLocaleString('es-AR', { month: 'long' }).toUpperCase();

        return {
            desde: `${year}-${month}-01`,
            hasta: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
            nombreMes,
            year,
            labelRango: `${nombreMes} 1 AL ${lastDay}`
        };
    }, []);

    const cargarProduccionMensual = useCallback(async (forceRefresh = false) => {
        const now = Date.now();
        const lastFetch = produccionMensualCache?.lastFetch || 0;
        const isCacheValid = (now - lastFetch) < CACHE_TTL_MS;

        // Si la caché es válida y no se forzó actualización manual, NO consulta a Firestore
        if (!forceRefresh && produccionMensualCache?.data && isCacheValid) {
            return;
        }

        // Si no hay datos previos, activamos el spinner/skeleton inicial
        if (!produccionMensualCache?.data) {
            setLoadingProduccionMensual(true);
        }

        try {
            const { desde, hasta } = rangoMesActual;

            const qEmisiones = query(
                collectionGroup(db, 'polizas'),
                where('fechaCarga', '>=', desde),
                where('fechaCarga', '<=', hasta)
            );

            const snapEmisiones = await getDocs(qEmisiones);

            const altas = snapEmisiones.docs.map(docSnap => {
                const pData = docSnap.data();
                
                const estadoEmision = String(pData.estadoEmision || pData.estadoPoliza || '').trim().toUpperCase();
                const esPolizaAnulada = ['ANULADA', 'CANCELADA', 'RECHAZADA'].includes(estadoEmision) || !!pData.fechaAnulacion;
                
                if (esPolizaAnulada) return null;

                const motivo = String(pData.motivo || pData.tipoOperacion || '').trim().toUpperCase();
                const subOperacion = String(pData.subOperacion || '').trim().toUpperCase();

                const esEmisionNueva = 
                    (motivo.includes('PÓLIZA NUEVA') || motivo.includes('POLIZA NUEVA') || motivo.includes('NUEVA') || motivo.includes('EMISION')) &&
                    (subOperacion.includes('EMISIÓN NUEVA') || subOperacion.includes('EMISION NUEVA') || subOperacion === '');

                if (!esEmisionNueva) return null;

                const primaClean = parseCurrency(pData.primaEmitida || pData.premio || 0);
                const moneda = String(pData.moneda || '$').trim().toUpperCase();
                const isUSD = moneda === 'USD' || moneda === 'U$S' || moneda === 'US$';

                return {
                    id: docSnap.id,
                    vendedor: String(pData.vendedor || 'SIN ASIGNAR').trim().toUpperCase(),
                    compania: String(pData.compania || 'SIN COMPAÑÍA').trim().toUpperCase(),
                    riesgo: String(pData.riesgo || 'SIN RIESGO').trim().toUpperCase(),
                    primaEmitida: primaClean,
                    moneda: isUSD ? 'USD' : 'ARS'
                };
            }).filter(Boolean);

            // Guardamos los datos junto con la marca de tiempo de la consulta
            setProduccionMensualCache({
                data: altas,
                lastFetch: Date.now()
            });

        } catch (err) {
            console.error("Error cargando producción del mes en Widget:", err);
        } finally {
            setLoadingProduccionMensual(false);
        }
    }, [rangoMesActual, produccionMensualCache, setProduccionMensualCache, setLoadingProduccionMensual]);

    useEffect(() => {
        cargarProduccionMensual(false);
    }, []);

    const polizasEmitidas = produccionMensualCache?.data || [];

    const rankings = useMemo(() => {
        const vendedoresMap = {};
        const companiasMap = {};
        const riesgosMap = {};

        let totalPrimaARS = 0;
        let totalPrimaUSD = 0;

        polizasEmitidas.forEach(p => {
            if (p.moneda === 'USD') totalPrimaUSD += p.primaEmitida;
            else totalPrimaARS += p.primaEmitida;

            if (!vendedoresMap[p.vendedor]) {
                vendedoresMap[p.vendedor] = { nombre: p.vendedor, cantidad: 0, primaARS: 0, primaUSD: 0 };
            }
            vendedoresMap[p.vendedor].cantidad += 1;
            if (p.moneda === 'USD') vendedoresMap[p.vendedor].primaUSD += p.primaEmitida;
            else vendedoresMap[p.vendedor].primaARS += p.primaEmitida;

            if (!companiasMap[p.compania]) {
                companiasMap[p.compania] = { nombre: p.compania, cantidad: 0, primaARS: 0, primaUSD: 0 };
            }
            companiasMap[p.compania].cantidad += 1;
            if (p.moneda === 'USD') companiasMap[p.compania].primaUSD += p.primaEmitida;
            else companiasMap[p.compania].primaARS += p.primaEmitida;

            if (!riesgosMap[p.riesgo]) {
                riesgosMap[p.riesgo] = { nombre: p.riesgo, cantidad: 0, primaARS: 0 };
            }
            riesgosMap[p.riesgo].cantidad += 1;
            if (p.moneda === 'ARS') riesgosMap[p.riesgo].primaARS += p.primaEmitida;
        });

        const totalPolizasNetas = polizasEmitidas.length;

        const rankingVendedores = Object.values(vendedoresMap).sort((a, b) => b.primaARS - a.primaARS);
        const rankingCompanias = Object.values(companiasMap).sort((a, b) => b.primaARS - a.primaARS);
        const rankingRiesgos = Object.values(riesgosMap).sort((a, b) => b.primaARS - a.primaARS);

        return {
            totalPolizasNetas,
            totalPrimaARSFormatted: formatCurrencyARS(totalPrimaARS),
            totalPrimaUSDFormatted: formatCurrencyUSD(totalPrimaUSD),
            cantidadEmitidas: totalPolizasNetas,
            rankingVendedores,
            rankingCompanias,
            rankingRiesgos
        };
    }, [polizasEmitidas]);

    return {
        rangoMesActual,
        loading: loadingProduccionMensual,
        hasCache: !!produccionMensualCache?.data,
        rankings,
        refetch: () => cargarProduccionMensual(true) // Forzado manual
    };
};